import React from 'react';
import { Zap, ShieldCheck, Activity, BarChart3, Globe } from 'lucide-react';
import { CodeBlock } from '../components/CodeBlock';

const Home: React.FC = () => {
  return (
    <div className="space-y-6 animate-in fade-in duration-700">
      <div className="space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-semibold uppercase tracking-wider">
          <Zap size={14} fill="currentColor" /> Welcome to Cyxor v1.0
        </div>
        <h1 className="text-5xl sm:text-7xl font-black tracking-tighter text-slate-900 leading-[0.9]">
          Cyxor <span className="text-primary italic">Collector</span>
        </h1>
        <p className="text-xl text-slate-600 leading-relaxed max-w-[60ch] font-medium">
          The official unified telemetry SDK for streaming logs, performance data, and heartbeats into the Cyxor ecosystem.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-12">
        <div className="glass p-6 rounded-2xl group hover:border-primary/50 transition-all duration-300 shadow-lg shadow-black/20 hover:shadow-primary/10">
          <Activity className="text-primary mb-4" size={32} />
          <h3 className="text-xl font-black text-slate-900 tracking-tight">Real-time Telemetry</h3>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed font-medium">
            Non-blocking ingestion for logs, CPU, memory, and custom business metrics.
          </p>
        </div>
        <div className="glass p-6 rounded-2xl group hover:border-blue-500/50 transition-all duration-300">
          <ShieldCheck className="text-blue-500 mb-4" size={32} />
          <h3 className="text-xl font-black text-slate-900 tracking-tight">SOC2 Compliant</h3>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed font-medium">
            Immutable audit logs and cryptographically verified integrity for compliance readiness.
          </p>
        </div>
      </div>

      <section className="space-y-4 pt-12">
        <h2 className="text-3xl font-black text-slate-900 tracking-tighter">Quick Start</h2>
        <p className="text-slate-600 leading-relaxed font-medium">
          Cyxor Collector provides a uniform way to gather data across various languages and environments. Whether you're running on Vercel, cPanel, or a bare-metal server, Cyxor ensures you never lose a log.
        </p>
        <CodeBlock 
          language="bash" 
          code="npm install @cyxor/collector" 
          filename="Terminal"
        />
      </section>

      <section className="pt-12 grid grid-cols-1 gap-12 border-t border-slate-100 mt-12">
        <div className="space-y-4">
          <h2 className="text-3xl font-black text-slate-900 tracking-tighter">Why Cyxor?</h2>
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center flex-shrink-0">
                <Globe size={20} className="text-primary" />
              </div>
              <div className="space-y-1">
                <h4 className="font-extrabold text-slate-900">Edge-First Architecture</h4>
                <p className="text-sm text-slate-500 font-medium leading-relaxed">Optimized for serverless environments (Vercel, Cloudflare) where traditional agents won't run.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center flex-shrink-0">
                <BarChart3 size={20} className="text-blue-500" />
              </div>
              <div className="space-y-1">
                <h4 className="font-extrabold text-slate-900">Dynamic Batching</h4>
                <p className="text-sm text-slate-500 font-medium leading-relaxed">Smart queuing logic reduces network overhead while maintaining near real-time visibility.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
